import { GameMode, StorageLike, resolveStorage } from './gameStorage'

export const PLAYER_STATS_VERSION = 1 as const

export type PlayerStats = {
  version: typeof PLAYER_STATS_VERSION
  totalSolved: number
  dailySolved: number
  practiceSolved: number
  currentDailyStreak: number
  bestDailyStreak: number
  lastDailyIndex: number | null
  totalMistakes: number
  totalHints: number
  bestTimeSeconds: number | null
  completedPuzzleIds: string[]
}

export type PuzzleCompletionInput = {
  puzzleId: string
  mode: GameMode
  solutionIndex: number
  mistakes: number
  hintsUsed: number
  elapsedSeconds: number
}

const PLAYER_STATS_KEY = 'word-square-puzzle:player-stats'
const MAX_PUZZLE_ID_LENGTH = 200

const createDefaultPlayerStats = (): PlayerStats => ({
  version: PLAYER_STATS_VERSION,
  totalSolved: 0,
  dailySolved: 0,
  practiceSolved: 0,
  currentDailyStreak: 0,
  bestDailyStreak: 0,
  lastDailyIndex: null,
  totalMistakes: 0,
  totalHints: 0,
  bestTimeSeconds: null,
  completedPuzzleIds: [],
})

const defaultPlayerStats = createDefaultPlayerStats()
Object.freeze(defaultPlayerStats.completedPuzzleIds)
Object.freeze(defaultPlayerStats)

/** A read-only-at-runtime template. Loading always returns a fresh copy. */
export const DEFAULT_PLAYER_STATS: PlayerStats = defaultPlayerStats

const copyPlayerStats = (stats: PlayerStats): PlayerStats => ({
  ...stats,
  completedPuzzleIds: [...stats.completedPuzzleIds],
})

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const isNonNegativeInteger = (value: unknown): value is number =>
  typeof value === 'number' && Number.isSafeInteger(value) && value >= 0

const isNonNegativeFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value >= 0

const isPuzzleId = (value: unknown): value is string =>
  typeof value === 'string' &&
  value.length > 0 &&
  value.length <= MAX_PUZZLE_ID_LENGTH &&
  value.trim() === value

const isCompletedPuzzleIds = (value: unknown): value is string[] =>
  Array.isArray(value) &&
  value.every(isPuzzleId) &&
  new Set(value).size === value.length

const hasConsistentCounts = (value: Record<string, unknown>) =>
  (value.totalSolved as number) ===
    (value.dailySolved as number) + (value.practiceSolved as number) &&
  (value.completedPuzzleIds as string[]).length === value.totalSolved &&
  (value.currentDailyStreak as number) <= (value.bestDailyStreak as number) &&
  (value.bestDailyStreak as number) <= (value.dailySolved as number) &&
  (value.dailySolved === 0
    ? value.lastDailyIndex === null
    : value.lastDailyIndex !== null) &&
  (value.totalSolved === 0
    ? value.bestTimeSeconds === null
    : value.bestTimeSeconds !== null)

const isPlayerStats = (value: unknown): value is PlayerStats => {
  try {
    return (
      isRecord(value) &&
      value.version === PLAYER_STATS_VERSION &&
      isNonNegativeInteger(value.totalSolved) &&
      isNonNegativeInteger(value.dailySolved) &&
      isNonNegativeInteger(value.practiceSolved) &&
      isNonNegativeInteger(value.currentDailyStreak) &&
      isNonNegativeInteger(value.bestDailyStreak) &&
      (value.lastDailyIndex === null ||
        isNonNegativeInteger(value.lastDailyIndex)) &&
      isNonNegativeInteger(value.totalMistakes) &&
      isNonNegativeInteger(value.totalHints) &&
      (value.bestTimeSeconds === null ||
        isNonNegativeFiniteNumber(value.bestTimeSeconds)) &&
      isCompletedPuzzleIds(value.completedPuzzleIds) &&
      hasConsistentCounts(value)
    )
  } catch (_error) {
    return false
  }
}

const isCompletionInput = (value: unknown): value is PuzzleCompletionInput => {
  try {
    return (
      isRecord(value) &&
      isPuzzleId(value.puzzleId) &&
      (value.mode === 'daily' || value.mode === 'practice') &&
      isNonNegativeInteger(value.solutionIndex) &&
      isNonNegativeInteger(value.mistakes) &&
      isNonNegativeInteger(value.hintsUsed) &&
      isNonNegativeFiniteNumber(value.elapsedSeconds)
    )
  } catch (_error) {
    return false
  }
}

/** Loads saved stats, falling back to a fresh default for missing/corrupt data. */
export const loadPlayerStats = (storage?: StorageLike | null): PlayerStats => {
  const target = resolveStorage(storage)
  if (!target) {
    return createDefaultPlayerStats()
  }

  try {
    const serialized = target.getItem(PLAYER_STATS_KEY)
    if (serialized === null) {
      return createDefaultPlayerStats()
    }
    const parsed: unknown = JSON.parse(serialized)
    return isPlayerStats(parsed)
      ? copyPlayerStats(parsed)
      : createDefaultPlayerStats()
  } catch (_error) {
    return createDefaultPlayerStats()
  }
}

/** Saves only complete, internally consistent stats records. */
export const savePlayerStats = (
  stats: PlayerStats,
  storage?: StorageLike | null
): boolean => {
  if (!isPlayerStats(stats)) {
    return false
  }

  const target = resolveStorage(storage)
  if (!target) {
    return false
  }

  try {
    target.setItem(PLAYER_STATS_KEY, JSON.stringify(stats))
    return true
  } catch (_error) {
    return false
  }
}

/**
 * Returns updated lifetime stats for a newly completed puzzle. A puzzle id is
 * counted at most once, so re-opening a completed puzzle is harmless.
 */
export const recordPuzzleCompletion = (
  stats: PlayerStats,
  input: PuzzleCompletionInput
): PlayerStats => {
  const unchanged = () => copyPlayerStats(stats)

  if (
    !isPlayerStats(stats) ||
    !isCompletionInput(input) ||
    stats.completedPuzzleIds.includes(input.puzzleId)
  ) {
    return unchanged()
  }

  const totalSolved = stats.totalSolved + 1
  const dailySolved = stats.dailySolved + (input.mode === 'daily' ? 1 : 0)
  const practiceSolved =
    stats.practiceSolved + (input.mode === 'practice' ? 1 : 0)
  const totalMistakes = stats.totalMistakes + input.mistakes
  const totalHints = stats.totalHints + input.hintsUsed

  if (
    !Number.isSafeInteger(totalSolved) ||
    !Number.isSafeInteger(dailySolved) ||
    !Number.isSafeInteger(practiceSolved) ||
    !Number.isSafeInteger(totalMistakes) ||
    !Number.isSafeInteger(totalHints)
  ) {
    return unchanged()
  }

  let currentDailyStreak = stats.currentDailyStreak
  let bestDailyStreak = stats.bestDailyStreak
  let lastDailyIndex = stats.lastDailyIndex

  if (
    input.mode === 'daily' &&
    (lastDailyIndex === null || input.solutionIndex > lastDailyIndex)
  ) {
    currentDailyStreak =
      lastDailyIndex !== null && input.solutionIndex === lastDailyIndex + 1
        ? currentDailyStreak + 1
        : 1
    bestDailyStreak = Math.max(bestDailyStreak, currentDailyStreak)
    lastDailyIndex = input.solutionIndex
  }

  return {
    version: PLAYER_STATS_VERSION,
    totalSolved,
    dailySolved,
    practiceSolved,
    currentDailyStreak,
    bestDailyStreak,
    lastDailyIndex,
    totalMistakes,
    totalHints,
    bestTimeSeconds:
      stats.bestTimeSeconds === null
        ? input.elapsedSeconds
        : Math.min(stats.bestTimeSeconds, input.elapsedSeconds),
    completedPuzzleIds: [...stats.completedPuzzleIds, input.puzzleId],
  }
}
